import type {
  MultiSelectProps,
  DropdownSelectProps,
  SingleSelectProps,
  DropdownSelectOption
} from '@paintscout/ui-business'
import { getFeature, getObjectLabels, getQuoteTypeOptions } from '@paintscout/util/builder'
import { DropdownSelect, useClientOptions } from '@paintscout/ui-business'
import React from 'react'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface QuoteTypeSelectProps extends Omit<DropdownSelectProps, 'value' | 'options' | 'onChange'> {
  value: string[]
  hideAll?: boolean
  /**
   * Label to use for "all" quote type. Set to falsey to fallback on label in options
   */
  allLabel?: string
  onChange?: (value: string[]) => any
}

export default function QuoteTypeSelect({ value, allLabel, onChange, ...props }: QuoteTypeSelectProps) {
  const { options } = useClientOptions()
  const { quote: quoteLabel } = getObjectLabels({ options })
  const { canDeactivateSettingsItems } = useFlags()
  const hideAll = getFeature({ options, path: 'quotes.hideAllQuoteType' }) || props.hideAll
  const typeOptions = getQuoteTypeOptions({ options, hideAll, canDeactivate: !!canDeactivateSettingsItems })
    .map((type) => ({
      value: type.key,
      label: type.key === 'all' && allLabel ? allLabel : type.label,
      forceTop: type.key === 'all'
    }))
    .sort((a) => (a.value === 'all' ? -1 : 1))

  const singleSelectProps = {
    ...props,
    value: typeOptions.find((opt) => value.includes(opt.value)),
    onChange: (option: DropdownSelectOption) => {
      onChange?.([option.value])
    },
    variant: 'single',
    options: typeOptions
  } as SingleSelectProps

  // Once the 'all' sentinel option is hidden (hideAll), an empty `value` is the only way left to
  // represent "applies to every quote type" — get-presentation-options.ts's `!quoteTypes.length`
  // and normalize-presentation.ts both already treat it that way. But the dropdown displayed it as
  // fully UNselected while the placeholder below still claimed "All ... types", so "Select All" and
  // every option showed unchecked in a state that already meant "all" (#17116/ENG-1344). Selecting
  // every option here, rather than leaving `value` empty, makes the checkboxes agree with what's
  // already true; unchecking any one of them still writes back a real, explicit id list via onChange,
  // same as an actual "Select All" click always has.
  const multiSelectProps = {
    ...props,
    value: hideAll && !value.length ? typeOptions : typeOptions.filter((opt) => value.includes(opt.value)),
    onChange: (options: DropdownSelectOption[]) => {
      onChange?.(options.map((option) => option.value))
    },
    variant: 'multi',
    options: typeOptions
  } as MultiSelectProps

  const selectProps = props.variant === 'multi' ? multiSelectProps : singleSelectProps

  return (
    <DropdownSelect
      placeholder={{ selectPlaceholder: `All ${quoteLabel.value.toLowerCase()} types` }}
      hideCloseButton
      {...selectProps}
    />
  )
}
